import { AlertTriangle, CheckCircle2, CircleAlert, Info, X } from "lucide-react";
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type CSSProperties, type ReactNode } from "react";

export type AdminToastType = "success" | "error" | "warning" | "info";

interface AdminToastInput {
  type: AdminToastType;
  title: string;
  message?: string;
  duration?: number;
}

interface AdminToast extends AdminToastInput {
  id: string;
  duration: number;
}

interface AdminToastContextValue {
  showToast: (toast: AdminToastInput) => void;
}

const AdminToastContext = createContext<AdminToastContextValue | null>(null);

const toastIcons = {
  success: CheckCircle2,
  error: CircleAlert,
  warning: AlertTriangle,
  info: Info,
};

export function AdminToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<AdminToast[]>([]);

  const dismissToast = useCallback((id: string) => {
    setToasts((current) => current.filter((toast) => toast.id !== id));
  }, []);

  const showToast = useCallback((input: AdminToastInput) => {
    const toast: AdminToast = {
      ...input,
      id: crypto.randomUUID(),
      duration: input.duration ?? (input.type === "error" ? 7200 : 4800),
    };
    setToasts((current) => [...current.slice(-3), toast]);
  }, []);

  const value = useMemo(() => ({ showToast }), [showToast]);

  return (
    <AdminToastContext.Provider value={value}>
      {children}
      <div className="admin-toast-viewport" aria-live="polite" aria-relevant="additions">
        {toasts.map((toast) => (
          <AdminToastItem key={toast.id} toast={toast} onDismiss={dismissToast} />
        ))}
      </div>
    </AdminToastContext.Provider>
  );
}

function AdminToastItem({ toast, onDismiss }: { toast: AdminToast; onDismiss: (id: string) => void }) {
  const Icon = toastIcons[toast.type];

  useEffect(() => {
    const timeout = window.setTimeout(() => onDismiss(toast.id), toast.duration);
    return () => window.clearTimeout(timeout);
  }, [onDismiss, toast.duration, toast.id]);

  return (
    <article
      className={`admin-toast admin-toast--${toast.type}`}
      role={toast.type === "error" ? "alert" : "status"}
      style={{ "--toast-duration": `${toast.duration}ms` } as CSSProperties}
    >
      <span className="admin-toast-icon"><Icon /></span>
      <div><strong>{toast.title}</strong>{toast.message && <p>{toast.message}</p>}</div>
      <button type="button" onClick={() => onDismiss(toast.id)} aria-label="Meldung schließen"><X /></button>
      <span className="admin-toast-progress" aria-hidden="true" />
    </article>
  );
}

export function useAdminToast() {
  const value = useContext(AdminToastContext);
  if (!value) throw new Error("useAdminToast must be used within AdminToastProvider");
  return value;
}
